import { ReactNode, useEffect } from 'react'
import { Box, Flex } from '@storyofams/react-ui'
import { FormattedMessage } from 'react-intl'
import { Heading, Text, Button } from '~components'

import { formatPrice } from '~lib'
import { StaticQuantityInput } from '~components/common/Cart/StaticQuantityInput'
import { SelectOptions, Select } from '~components/common/Select'

interface BoxRowProps {
  title: string
  amount: number
  updateStaticAmount?: (value: number) => void
  options: SelectOptions
  setSelectedOption: (value: string) => void
  isOptionDisabled: (value: any) => void
  selectedVariant: Record<string, any>
  children: ReactNode
  OneVariant: any
}

export const StaticBoxRow = ({
  title,
  amount,
  options,
  isOptionDisabled,
  setSelectedOption,
  updateStaticAmount,
  selectedVariant,
  children,
  OneVariant,
}: BoxRowProps) => {
  const scrollToBuySection = () => {
    const el = document?.getElementById('buy-box')
    el?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      {/* mobile */}
      <Flex
        width="100%"
        display={['flex', 'flex', 'none !important']}
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading variant="h5" as="h5">
          {title}
        </Heading>
        {children}
      </Flex>

      {/* desktop */}
      <Box display={['none !important', 'none !important', 'flex !important']}>
        <Flex flex={1} alignItems="center">
          <Flex
            alignItems="center"
            pr={5}
            height="48px"
            borderRight="2px solid #21857F"
          >
            <Heading variant="sh4" as="h4">
              {title}
            </Heading>
          </Flex>

          <Flex flex={1} px={5} alignItems="center">
            {options && options?.length > 1 ? (
              <Select
                flexGrow={1}
                width="200px"
                options={options}
                isOptionDisabled={isOptionDisabled}
                onChange={({ value }) => setSelectedOption(value)}
                mr={2}
                menuPlacement="top"
                value={options.find(
                  option => option.value === selectedVariant.id,
                )}
                customStyles={{
                  '.react-select': {
                    '&__single-value': {
                      color: '#161615',
                      textTransform: 'uppercase',
                    },
                    '&__indicators svg': {
                      fill: '#161615',
                    },
                    '&__control': {
                      borderRadius: '0',
                      borderWidth: '1.5px',
                      borderColor: '#21857F',
                      fontSize: 2,
                      height: '45px',
                      backgroundColor: '#FAF7F5',
                      '&:hover': {
                        cursor: 'pointer',
                        bg: 'grey300',
                        borderColor: '#21857F',
                        backgroundColor: '#FAF7F5',
                      },
                      '&--menu-is-open': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        borderTop: `1px solid #21857F`,
                      },
                    },
                    '&__menu': {
                      zIndex: 2,
                      my: 0,
                      borderRadius: '0',
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      borderWidth: '1.5px',
                      borderStyle: 'solid',
                      borderColor: '#21857F',
                      borderBottom: 'none',
                      boxShadow: 'none',
                      overflow: 'hidden',
                      backgroundColor: '#FAF7F5',
                      '.react-select__menu-list': {
                        p: 0,
                      },
                      '.react-select__option': {
                        px: 2,
                        py: 1,
                        mb: 0.2,
                        color: '#161615',
                        textTransform: 'uppercase',
                        '&:hover': {
                          cursor: 'pointer',
                        },
                        '&:hover, &:focus, &--is-selected': {
                          backgroundColor: '#FAF7F5',
                        },
                        '&--is-disabled': {
                          color: 'grey400',
                          cursor: 'not-allowed',
                        },
                      },
                    },
                  },
                }}
              />
            ) : (
              <OneVariant />
            )}
          </Flex>
        </Flex>

        <Flex height="100%" alignItems="center" justifyContent="flex-end">
          <StaticQuantityInput
            quantity={amount}
            variant={selectedVariant as any}
            updateStaticAmount={updateStaticAmount}
            small
            mx={0}
            mr={5}
            pr={5}
            height="48px"
            borderRight="2px solid #21857F"
          />

          <Flex flexDirection={'column'} mr={8}>
            <Heading variant="sh3" as="h3" fontSize={'30px'}>
              {formatPrice(selectedVariant.priceV2)}
            </Heading>
            {selectedVariant?.compareAtPriceV2 ? (
              <Heading
                variant="sh3"
                as="h3"
                fontSize={'20px'}
                css={{
                  textDecorationLine: 'line-through',
                }}
                color="grey500"
              >
                {formatPrice(selectedVariant.compareAtPriceV2)}
              </Heading>
            ) : (
              ''
            )}
          </Flex>
          {children}
        </Flex>
      </Box>
    </>
  )
}
